/**
 * Suggestions — contextual auto-suggestion pills
 *
 * Renders clickable pill buttons after assistant messages.
 * Supports outlined/filled variants, loading skeleton, and horizontal scroll.
 */

import { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import './Suggestions.css';

const SKELETON_COUNT = 3;

export const Suggestions = memo(({
  suggestions = [],
  onSelect,
  maxVisible = 3,
  accentColor,
  variant = 'outlined',
  visible = true,
  loading = false,
}) => {
  // Declared before the early return: hooks must run on every render of this
  // component, and `suggestions` starts empty, so a `return null` above the
  // hook would change the hook count once suggestions arrive.
  const handleClick = useCallback((text, index) => {
    console.log('[Suggestions] clicked:', { index, text });
    onSelect?.(text, index);
  }, [onSelect]);

  // Nothing to render
  if (suggestions.length === 0 && !loading) return null;

  const visibleSuggestions = suggestions.slice(0, maxVisible);

  const containerClass = [
    'suggestions',
    visible && 'suggestions--visible',
  ].filter(Boolean).join(' ');

  // Apply accent color override via CSS custom property
  const style = accentColor ? { '--suggestions-accent': accentColor } : undefined;

  return (
    <div className={containerClass} style={style}>
      <div className="suggestions__list" role="group" aria-label="Suggested replies">
        {loading && suggestions.length === 0 ? (
          // Skeleton loading state
          Array.from({ length: SKELETON_COUNT }, (_, i) => (
            <div
              key={`skeleton-${i}`}
              className="suggestions__pill suggestions__pill--skeleton"
              aria-hidden="true"
            />
          ))
        ) : (
          // Actual suggestion pills
          visibleSuggestions.map((text, index) => (
            <button
              key={`${text}-${index}`}
              type="button"
              className={`suggestions__pill suggestions__pill--${variant}`}
              onClick={() => handleClick(text, index)}
              title={text}
            >
              {text}
            </button>
          ))
        )}
      </div>
    </div>
  );
});

Suggestions.displayName = 'Suggestions';

Suggestions.propTypes = {
  suggestions: PropTypes.arrayOf(PropTypes.string),
  onSelect: PropTypes.func,
  maxVisible: PropTypes.number,
  accentColor: PropTypes.string,
  variant: PropTypes.oneOf(['outlined', 'filled']),
  visible: PropTypes.bool,
  loading: PropTypes.bool,
};
